/*****************************************************************************
 *   © Copyright 2021   Quantitative Software Management, Inc.               *
 *              --  All rights reserved.  --                                 *
 *   Unpublished -- rights reserved under the Copyright Laws                 *
 *   of the United States.                                                   *
 *                                                                           *
 *   This computer software is protected by trade secret, copyright law,     *
 *   and international treaties and contains proprietary and confidential    *
 *   trade secrets of Quantitative Software Management, Inc.  Unauthorized   *
 *   use, reproduction, or distribution of this program or any portion of    *
 *   it will result in severe civil or criminal penalties and will be        *
 *   prosecuted to the full extent of the law.                               *
 *                                                                           *
 *   Use, duplication, or disclosure by the Government is subject to         *
 *   restrictions as set forth in                                            *
 *      (i) a license agreement incorporated in the contract,                *
 *      (ii) the definition of "restricted rights" in paragraph (c)(1)(ii)   *
 *           of DFARS 252.227-7013 (1988), and                               *
 *      (iii) the definition of "restricted rights" in paragraph (a) of      *
 *            FAR 52.227-14, Alt III (Jun 1987) or                           *
 *      (iv) FAR 52.227-19 (Jun 1987), as applicable.                        *
 *   For purposes of the FAR, this software shall be deemed to               *
 *   be "unpublished" and licensed with disclosure prohibitions.             *
 *   Contractor/Manufacturer is   Quantitative Software Management, Inc.     *
 *                                                                           *
 *****************************************************************************/

export enum Assessment { UNKNOWN, STARS1, STARS2, STARS3, STARS4, STARS5 }
export enum MetricRatingMode { HIGH_IS_GOOD = 1, AVG_IS_GOOD, LOW_IS_GOOD }
export enum FunctionUnit { SLOC, FUNCTION_PTS }
export enum DefectCategory { DEFECT1, DEFECT2, DEFECT3, DEFECT4, DEFECT5, DEFECT_TTL }
export enum CurveFitType { POWER = 1, LINEAR, EXPONENTIAL, LOGARITHMIC }
export enum SizeSource { BIN = 0, DIRECT, CALCULATOR };
export enum ProductivityType { RANK = 0, INDEX = 1 };
export enum SolutionMethod { TREND = 0, CONSTRAINED = 1, SOLVEFORPI, BOXED }
export enum SolutionParameter { NONE = -1, MBI = 0, DURATION, EFFORT, COST, STAFF, PEAK, P3COST }
export enum SizeChangeType { CONVERT = 1, RECALIBRATE };
export enum TrendMode { USE_QSM_APP_TREND = 0, USE_TREND_GROUP }
export enum SizingType { USER_UNIT_SIZING = 0, BASE_UNIT_SIZING }
export enum ConfigSetType { ALL = 0, PHASE = 1, RELIABILITY = 2, MILESTONE = 4, SIZING = 8 }
export enum ProjectTab { Main, Assumptions, Sizing, Description, Access }   //must stay in sync with UI
export enum CloseoutTab { Main, Actuals, Sizing, Description, Access }   //must stay in sync with UI
export enum TemplateTab { Main, Phases, Description, Access }
export enum ProjectStatus { NORMAL = 0, LOCKED }
export enum Phase { SL_PHASE1 = 0, SL_PHASE2 = 1, SL_PHASE3 = 2, SL_PHASE4 = 3, SL_LIFE = 4 }
export enum ProjectAccessMode { EVERYONE = 1, NO_ONE = 2, OBS = 3, OBS_LIST = 4, LIST = 5 }
export enum SolutionType { CONTINGENCY = -4, TRENDBASED = -3, ALL = -1, NONE = 0, ESTIMATION = 2, CLOSEOUT = 5 }
export enum ServiceType { NONE = 0, ESTIMATION = SolutionType.ESTIMATION, CLOSEOUT = SolutionType.CLOSEOUT, BENCHMARK = 6, PORTFOLIO = 7 }
export enum TimeSlice { None = 0, Day, Week, Month, Quarter, Year, TwoYear, FiveYear, TenYear }    // 'None' is 'default' value

export enum ContingencySoln { NONE = 0, CONTINGENCY_ONLY, CURRENT_ONLY, BOTH }
export const ContingencySolnLabel = new Map<number, string>([
  //[ContingencySoln.NONE, 'None'],
  [ContingencySoln.CONTINGENCY_ONLY, 'Contingency Only'],
  [ContingencySoln.CURRENT_ONLY, 'Current Solution Only'],
  [ContingencySoln.BOTH, 'Both']
]);

export enum DashboardType { ESTIMATION = SolutionType.ESTIMATION, CLOSEOUT = SolutionType.CLOSEOUT, PORTFOLIO = ServiceType.PORTFOLIO, CONTINGENCY = 10 }
export const DashboardTypeLabel = new Map<number, string>([
  [DashboardType.ESTIMATION, 'Estimation'],
  [DashboardType.CLOSEOUT, 'Closeout'],
  [DashboardType.CONTINGENCY, 'Contingency'],
  [DashboardType.PORTFOLIO, 'Portfolio']
]);

export enum ProductivityRank { UNKNOWN = -1, MUCH_LOWER_THAN_AVERAGE, LOWER_THAN_AVERAGE, AVERAGE, HIGHER_THAN_AVERAGE, MUCH_HIGHER_THAN_AVERAGE }
export const ProductivityRankLabel = new Map<number, string>([
  [ProductivityRank.UNKNOWN, 'Unknown'],
  [ProductivityRank.MUCH_LOWER_THAN_AVERAGE, 'Much lower than Average'],
  [ProductivityRank.LOWER_THAN_AVERAGE, 'Lower than Average'],
  [ProductivityRank.AVERAGE, 'Average'],
  [ProductivityRank.HIGHER_THAN_AVERAGE, 'Higher than Average'],
  [ProductivityRank.MUCH_HIGHER_THAN_AVERAGE, 'Much higher than Average'],
]);

export enum Stage { NONE = 0, ESTIMATION = 2, CLOSEOUT = 5 }
export const StageLabel = new Map<number, string>([
  [Stage.NONE, 'Unknown'],
  [Stage.CLOSEOUT, 'Closeout'],
  [Stage.ESTIMATION, 'Estimation']
]);

export enum SizeBin { VERYSMALL, SMALL, MEDIUM, MEDLARGE, LARGE, VERYLARGE };
export const SizeBinLabel = new Map<number, string>([
  [SizeBin.VERYSMALL, 'Very Small'],
  [SizeBin.SMALL, 'Small'],
  [SizeBin.MEDIUM, 'Medium'],
  [SizeBin.MEDLARGE, 'Medium Large'],
  [SizeBin.LARGE, 'Large'],
  [SizeBin.VERYLARGE, 'Very Large'],
]);

export enum SituationType {
  UNKNOWN = 0,
  BENCHMARK_TREND_METRICS = 1,
  BENCHMARK_ASSESSMENT_METRICS = 2,
  //		BENCHMARK_FIVESTAR_METRICS = 3,		// zzzASC - For Benchmark, FiveStar and Assessment metrics MUST be the same
  CLOSEOUT_TREND_METRICS = 4,
  CLOSEOUT_ASSESSMENT_METRICS = 5,
  ESTIMATION_TREND_METRICS = 6,
  //FEASIBILITY_ASSESSMENT_METRICS = 7,
  ESTIMATION_TIMESERIES_METRICS = 8,
  PORTFOLIO_NUMERIC_HISTOGRAM_METRICS = 9,
  PORTFOLIO_TEXT_HISTOGRAM_METRICS = 10,
  PORTFOLIO_TIMESERIES_METRICS = 11,
  PORTFOLIO_PIECHART_METRICS = 12,
  CLOSEOUT_SUMMARY_METRICS = 13,
  //FEASIBILITY_SUMMARY_METRICS = 14,
  ESTIMATION_SUMMARY_METRICS = 15,
  PORTFOLIO_LIST_METRICS = 16,
  ESTIMATION_SOLUTION_COMPARISON_METRICS = 17,
  ESTIMATION_ASSESSMENT_METRICS = 18,
  TREND_PHASE_TUNING = 19,
  CUSTOM_COLUMN = 20,
  SKILLS_TIMESERIES_METRICS = 21,
  SKILLS_BREAKOUT_METRICS = 22,
  QUADRANT_XY_METRICS = 23,
  QUADRANT_BUBBLE_METRICS = 24,
  ESTIMATION_ASSUMPTIONS_METRICS = 25,
  CLOSEOUT_ASSUMPTIONS_METRICS = 26,
  CONTINGENCY_PROBABILITY_METRICS = 27,
  PROJECT_OVERVIEW_METRICS = 28
}

export enum SituationClass {
  Unknown,
  Portfolio,
  Project,
  ProjectList
};
export const SituationTypeClass = new Map<number, number>([
  [SituationType.BENCHMARK_TREND_METRICS, SituationClass.Unknown],
  [SituationType.BENCHMARK_ASSESSMENT_METRICS, SituationClass.Portfolio],
  [SituationType.CLOSEOUT_TREND_METRICS, SituationClass.Project],
  [SituationType.CLOSEOUT_ASSESSMENT_METRICS, SituationClass.Project],
  [SituationType.ESTIMATION_TREND_METRICS, SituationClass.Project],
  [SituationType.ESTIMATION_TIMESERIES_METRICS, SituationClass.Project],
  [SituationType.PORTFOLIO_NUMERIC_HISTOGRAM_METRICS, SituationClass.Portfolio],
  [SituationType.PORTFOLIO_TEXT_HISTOGRAM_METRICS, SituationClass.Portfolio],
  [SituationType.PORTFOLIO_TIMESERIES_METRICS, SituationClass.Portfolio],
  [SituationType.PORTFOLIO_PIECHART_METRICS, SituationClass.Portfolio],
  [SituationType.CLOSEOUT_SUMMARY_METRICS, SituationClass.Project],
  [SituationType.ESTIMATION_SUMMARY_METRICS, SituationClass.Project],
  [SituationType.PORTFOLIO_LIST_METRICS, SituationClass.ProjectList],
  [SituationType.ESTIMATION_SOLUTION_COMPARISON_METRICS, SituationClass.Project],
  [SituationType.ESTIMATION_ASSESSMENT_METRICS, SituationClass.Project],
  [SituationType.TREND_PHASE_TUNING, SituationClass.Unknown],
  [SituationType.CUSTOM_COLUMN, SituationClass.ProjectList],
  [SituationType.SKILLS_TIMESERIES_METRICS, SituationClass.Project],
  [SituationType.SKILLS_BREAKOUT_METRICS, SituationClass.Project],
  [SituationType.QUADRANT_XY_METRICS, SituationClass.Portfolio],
  [SituationType.QUADRANT_BUBBLE_METRICS, SituationClass.Portfolio],
  [SituationType.ESTIMATION_ASSUMPTIONS_METRICS, SituationClass.Project],
  [SituationType.CLOSEOUT_ASSUMPTIONS_METRICS, SituationClass.Project],
  [SituationType.CONTINGENCY_PROBABILITY_METRICS, SituationClass.Project],
  [SituationType.PROJECT_OVERVIEW_METRICS, SituationClass.Project]
]);

//zzAF - the ones with numbers have been at least partially implemented (just so we can keep up with these temporarily)
export enum ChartType {
  Risk,
  Trend = 1,
  SolnComparison = 2,
  TimeSeries = 3,
  Milestones = 4,
  ProjectSummary = 5,
  SkillsTS = 6,
  SkillsBreakout = 7,
  Pie = 8,
  Histogram = 9,
  Gantt = 10,
  Quadrant = 11,
  FiveStar = 12,
  MetricBenchmark = 13,
  TrendBenchmark = 14,
  EstimateAssumptions = 15,
  CloseoutAssumptions = 16,
  SizeDetails = 17,
  Probability = 18,
  ProjectMetricAssessment = 19,
  ProjectOverview = 20
}

export const ChartTypeLabel = new Map<number, string>([
  [ChartType.Risk, 'Risk'],
  [ChartType.Trend, 'Trend'],
  [ChartType.SolnComparison, 'Comparison'],
  [ChartType.TimeSeries, 'Time Series'],
  [ChartType.Milestones, 'Milestones'],
  [ChartType.ProjectSummary, 'Solution Summary'],
  [ChartType.SkillsTS, 'Skills'],
  [ChartType.SkillsBreakout, 'Skills Breakout'],
  [ChartType.Pie, 'Pie'],
  [ChartType.Histogram, 'Bar Chart'],
  [ChartType.Gantt, 'Gantt'],
  [ChartType.Quadrant, 'Quadrant'],
  [ChartType.FiveStar, 'Five Star Benchmark'],
  [ChartType.MetricBenchmark, 'Metric Benchmark'],
  [ChartType.TrendBenchmark, 'Trend Benchmark'],
  [ChartType.EstimateAssumptions, 'Solution Assumptions'],
  [ChartType.CloseoutAssumptions, 'Closeout Assumptions'],
  [ChartType.SizeDetails, 'Size Details'],
  [ChartType.Probability, 'Probability'],
  [ChartType.ProjectMetricAssessment, 'Project Metric Assessment'],
  [ChartType.ProjectOverview, 'Project Overview']
]);

export enum TrendShadingStyle {
  DEFAULT = 0,
  REGION_5STAR,
  REGION_4STAR,
  REGION_3STAR,
  REGION_2STAR,
  REGION_1STAR,
  LINE_1SIGMA,
  LINE_2SIGMA,
  LINE_3SIGMA
}

export enum TrendLabelStyle {
  NONE = 0,
  PROJECTNAME,
  PROJECTID
}
